/**
 * Local notifications (plan doc 13).
 *
 * Scheduled by the client, opt-in per category, and never asked for on a page
 * load: `requestNotifications` runs only when a learner turns a category on,
 * which is the first moment they have said they want one. Push notifications
 * from the server are Phase 10; everything here is local to the device.
 *
 * Nothing in here throws on a browser without the API. A reminder that cannot
 * be scheduled is a reminder that does not happen, not a screen that breaks.
 */

export const NOTIFICATION_CATEGORIES = ['dailyReminder', 'streakAtRisk', 'questProgress'] as const;

export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number];
export type NotificationPrefs = Record<NotificationCategory, boolean>;

/** The plan's defaults: all of them useful, all of them on once asked for. */
export const DEFAULT_PREFS: NotificationPrefs = {
  dailyReminder: true,
  streakAtRisk: true,
  questProgress: true,
};

const KEY = 'audigoal.notifications';

export function readNotificationPrefs(): NotificationPrefs {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw === null) return { ...DEFAULT_PREFS };
    const parsed = JSON.parse(raw) as Partial<NotificationPrefs>;
    return { ...DEFAULT_PREFS, ...parsed };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

export function writeNotificationPrefs(prefs: NotificationPrefs): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(prefs));
  } catch {
    // Private mode or quota: the preference simply does not persist.
  }
}

export type PermissionState = 'granted' | 'denied' | 'default' | 'unsupported';

export function notificationPermission(): PermissionState {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission as PermissionState;
}

/** Asks the browser. Only ever called from a learner turning a category on. */
export async function requestNotifications(): Promise<PermissionState> {
  if (typeof Notification === 'undefined') return 'unsupported';
  if (Notification.permission !== 'default') return Notification.permission as PermissionState;
  try {
    return (await Notification.requestPermission()) as PermissionState;
  } catch {
    return 'denied';
  }
}

/**
 * The rotating daily lines (plan doc 13).
 *
 * Twenty of them, none guilt-tripping: "your uke misses you" is an invitation,
 * "you broke your promise" is a reason to delete the app.
 */
export const REMINDER_LINES: readonly string[] = [
  'Your ukulele misses you. Five minutes?',
  'A short one today? Two chords is a session.',
  'The metronome is warmed up and waiting.',
  'One lesson. You know how this goes.',
  'Ready when you are — no rush.',
  'Something small today beats something big never.',
  'Five minutes of music is still music.',
  'Tempo has a new rhythm for you.',
  'Fancy a tune before the day gets away?',
  'Your next lesson takes about four minutes.',
  'Clara has some notes she would like you to read.',
  'Volt is curious what you sound like today.',
  'A quick play? The hard part is picking it up.',
  'Even a scale counts.',
  'Sol says the ukulele is in a good mood.',
  'One song. That is the whole plan.',
  'Your ear is sharper than last week. Keep it there.',
  'Luna left the ocarina out for you.',
  'Otto suggests something in a comfortable key.',
  'Nothing urgent. Just here if you want to play.',
];

/** A line for a day, so the same one does not arrive twice running. */
export function reminderLine(dayIndex: number): string {
  return REMINDER_LINES[dayIndex % REMINDER_LINES.length] as string;
}

export interface Reminder {
  category: NotificationCategory;
  title: string;
  body: string;
}

/**
 * The reminder due now, if any.
 *
 * Pure, so the rules are testable without a clock or a browser: a daily nudge
 * at the chosen hour when nothing has been played, and a louder one at nine
 * when a streak worth keeping is about to lapse.
 */
export function dueReminder(context: {
  prefs: NotificationPrefs;
  hour: number;
  playedToday: boolean;
  streak: number;
  dayIndex: number;
  reminderHour: number | null;
}): Reminder | null {
  if (context.playedToday) return null;

  if (context.prefs.streakAtRisk && context.streak >= 3 && context.hour >= 21) {
    return {
      category: 'streakAtRisk',
      title: `🔥 ${context.streak}-day streak on the line`,
      body: 'One quick lesson keeps it alive.',
    };
  }

  if (
    context.prefs.dailyReminder &&
    context.reminderHour !== null &&
    context.hour === context.reminderHour
  ) {
    return {
      category: 'dailyReminder',
      title: 'Audigoal',
      body: reminderLine(context.dayIndex),
    };
  }

  return null;
}

/**
 * Shows a notification, if the browser will let us.
 *
 * Through the service worker's registration when there is one: Android Chrome
 * refuses `new Notification` from a page and only accepts
 * `registration.showNotification`, and a registration's notification also
 * outlives the tab. The page constructor is the fallback for browsers with no
 * worker, and a failure either way is silent by design — a reminder that
 * cannot be shown is a reminder that does not happen.
 */
export function notify(reminder: Reminder): void {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  const options: NotificationOptions = {
    body: reminder.body,
    tag: reminder.category,
    icon: '/icon-192.png',
  };
  const fromPage = (): void => {
    try {
      new Notification(reminder.title, options);
    } catch {
      // See above.
    }
  };
  if (typeof navigator === 'undefined' || navigator.serviceWorker === undefined) {
    fromPage();
    return;
  }
  navigator.serviceWorker
    .getRegistration()
    .then((registration) =>
      registration === undefined
        ? fromPage()
        : registration.showNotification(reminder.title, options),
    )
    .catch(fromPage);
}
